import {
  Id,
  NotValidatedToastProps,
  OnChangeCallback,
  Toast,
  ToastContainerProps,
  ToastContent,
  ToastProps
} from '../types';
import { canBeRendered, getAutoCloseDelay, isNum, parseClassName, toToastItem } from '../utils';

type Notify = () => void;

export type ContainerObserver = ReturnType<typeof createContainerObserver>;

export function createContainerObserver(
  id: Id,
  containerProps: ToastContainerProps,
  dispatchChanges: OnChangeCallback
) {
  let toastKey = 1;
  let toastCount = 0;
  let queue: Toast[] = [];
  let snapshot: Toast[] = [];
  let props = containerProps;
  const toasts = new Map<Id, Toast>();
  let toastQueueRef: Id[] = [];
  const listeners = new Set<Notify>();

  const observe = (notify: Notify) => {
    listeners.add(notify);
    return () => listeners.delete(notify);
  };

  const notify = () => {
    snapshot = Array.from(toasts.values());
    listeners.forEach(cb => cb());
  };

  const shouldIgnoreToast = ({ containerId, toastId, updateId }: NotValidatedToastProps) => {
    const containerMismatch = containerId ? containerId !== id : id !== 1;
    const isDuplicate = toasts.has(toastId) && updateId == null;

    return containerMismatch || isDuplicate;
  };

  const toggle = (v: boolean, id?: Id) => {
    toasts.forEach(t => {
      if (id == null || id === t.props.toastId) t.toggle?.(v);
    });
  };

  const markAsRemoved = (v: Toast) => {
    v.props?.onClose?.(v.removalReason);
    v.isActive = false;
  };

  const removeToast = (id?: Id) => {
    if (id == null) {
      toasts.forEach(markAsRemoved);
    } else {
      const t = toasts.get(id);
      if (t) markAsRemoved(t);
    }
    notify();
  };

  const clearQueue = () => {
    toastCount -= queue.length;
    queue = [];
  };

  const addActiveToast = (toast: Toast) => {
    const { toastId, updateId } = toast.props;
    const isNew = updateId == null;

    if (toast.staleId) {
      toasts.delete(toast.staleId);
      toastQueueRef = toastQueueRef.filter(id => id !== toast.staleId);
    }
    toast.isActive = true;

    toasts.set(toastId, toast);
    if (isNew) toastQueueRef.push(toastId);
    notify();
    dispatchChanges(toToastItem(toast, isNew ? 'added' : 'updated'));

    if (isNew) toast.props.onOpen?.();
  };

  const buildToast = <TData = unknown>(content: ToastContent<TData>, options: NotValidatedToastProps) => {
    if (shouldIgnoreToast(options)) return;

    const { toastId, updateId, data, staleId, delay } = options;

    const isNotAnUpdate = updateId == null;

    if (isNotAnUpdate) toastCount++;

    const toastProps = {
      ...props,
      style: props.toastStyle,
      key: toastKey++,
      ...Object.fromEntries(Object.entries(options).filter(([_, v]) => v != null)),
      toastId,
      updateId,
      data,
      isIn: false,
      className: parseClassName(options.className || props.toastClassName),
      progressClassName: parseClassName(options.progressClassName || props.progressClassName),
      autoClose: options.isLoading ? false : getAutoCloseDelay(options.autoClose, props.autoClose),
      closeToast(reason?: true) {
        toasts.get(toastId)!.removalReason = reason;
        removeToast(toastId);
      },
      deleteToast() {
        const toastToRemove = toasts.get(toastId);

        if (toastToRemove == null) return;

        dispatchChanges(toToastItem(toastToRemove, 'removed'));
        toasts.delete(toastId);
        toastQueueRef = toastQueueRef.filter(id => id !== toastId);

        toastCount--;
        if (toastCount < 0) toastCount = 0;

        if (queue.length > 0) {
          addActiveToast(queue.shift());
          return;
        }

        notify();
      }
    } as ToastProps;

    toastProps.closeButton = props.closeButton;

    if (options.closeButton === false || canBeRendered(options.closeButton)) {
      toastProps.closeButton = options.closeButton;
    } else if (options.closeButton === true) {
      toastProps.closeButton = canBeRendered(props.closeButton) ? props.closeButton : true;
    }

    const activeToast = {
      content,
      props: toastProps,
      staleId
    } as Toast;

    // not handling limit + delay by design. Waiting for user feedback first
    if (props.limit && props.limit > 0 && toastCount > props.limit && isNotAnUpdate) {
      queue.push(activeToast);
    } else if (isNum(delay) && delay > 0) {
      setTimeout(() => {
        addActiveToast(activeToast);
      }, delay);
    } else {
      addActiveToast(activeToast);
      if (props.maxShownToasts && toasts.size > props.maxShownToasts && isNotAnUpdate) {
        removeToast(toastQueueRef.shift());
      }
    }
  };

  return {
    id,
    props,
    observe,
    toggle,
    removeToast,
    toasts,
    clearQueue,
    buildToast,
    setProps(p: ToastContainerProps) {
      props = p;
    },
    setToggle: (id: Id, fn: (v: boolean) => void) => {
      const t = toasts.get(id);
      if (t) t.toggle = fn;
    },
    isToastActive: (id: Id) => toasts.get(id)?.isActive,
    getSnapshot: () => snapshot
  };
}
